/**
 * EnvironmentBanner — the "acting as" strip under the top bar. One consistent
 * pattern for every non-production environment that has an act-as concept, instead
 * of scattered standalone pills in the bar (the old persona / sandbox-user pills):
 *
 *   - Demo     → "Demo · Acting as <persona>" + Switch persona  (PersonaPicker)
 *   - Sandbox  → "Sandbox · Acting as <user>" + Switch user     (SandboxUserPicker)
 *   - Lingering client preview → "Previewing as X · Exit preview" (safety hatch;
 *     the preview entry point was retired — see previewClientActions)
 *   - dev / quality / production → nothing (no impersonation; real login + the
 *     environment pill on the bar carry the context).
 *
 * Treatment B, Tesseract-themed (brand-blue accent rail + chip). The always-on
 * demo persona reconcile effects live in usePersonaReconcile (Header); this is
 * just the surface that shows + changes the active identity.
 */
import { useState, useRef, useEffect, useSyncExternalStore } from 'react'
import { X, UserCog, ChevronDown } from 'lucide-react'
import { useAppStore } from '@/store/appStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { timesheetDataService } from '@/modules/timesheet/services/timesheetDataService'
import { isInternalBuild } from '@/config/productSurface'
import { listClientProducts } from '@/config/clientProducts'
import { getDemoCompany } from '@/types/demoCompanies'
import { getPreviewClientKey, subscribePreviewClient } from '@/config/previewClient'
import { exitClientPreview } from './previewClientActions'
import { PersonaPicker } from './PersonaPicker'
import { SandboxUserPicker } from './SandboxUserPicker'
import { DemoCompanySwitcher } from './DemoCompanySwitcher'
import { TunnelOrgChip } from './TunnelOrgChip'
import { deriveSystemRole } from './personaCast'
import { cn } from '@/lib/utils'

function friendlyRole(sys: string): string {
  if (sys === 'OrgAdmin' || sys === 'GlobalAdmin') return 'Admin'
  if (sys === 'Auditor') return 'Auditor'
  if (sys === 'Supervisor') return 'Manager'
  return 'Worker'
}

export function EnvironmentBanner() {
  const previewKey = useSyncExternalStore(subscribePreviewClient, getPreviewClientKey, () => null)
  const appMode = useAppStore((s) => s.appMode)
  const currentUser = useAppStore((s) => s.currentUser)
  const currentEmployeeId = useTimesheetStore((s) => s.currentEmployeeId)
  // Re-render when the Supabase roster lands so the persona name resolves.
  const referenceDataVersion = useAppStore((s) => s.referenceDataVersion)
  void referenceDataVersion
  const [menu, setMenu] = useState<null | 'persona'>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menu) return
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenu(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menu])

  // Internal build only (survives an active preview so you can exit).
  if (!isInternalBuild()) return null

  // Treatment B (accent rail), Tesseract-themed: white bar, a thin brand-blue rail
  // on the left + a solid brand "Demo" chip. Reads as native Tesseract chrome while
  // still marking the strip as a distinct context state.
  const railBar = 'flex items-center gap-2.5 px-4 py-2 bg-white border-b border-border border-l-[3px] border-l-brand-500 text-text-secondary text-body-sm'
  const chip = 'inline-flex items-center h-5 px-2 rounded bg-brand-500 text-white text-[10px] font-bold uppercase tracking-wider flex-shrink-0'
  const exitBtn = 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-brand-500 text-white text-[12.5px] font-semibold hover:bg-brand-600 active:scale-95 transition-colors'
  const ghostBtn = 'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-white border border-brand-200 text-brand-700 text-[12.5px] font-semibold hover:bg-brand-50 transition-colors'

  // ── Active client preview ──────────────────────────────────────────────
  if (previewKey) {
    const client = listClientProducts().find((c) => c.key === previewKey)
    // Fall back to the demo company's display name when the preview key is a demo
    // company id (the same-org "Client view" path) rather than a registered client.
    const previewLabel = client?.label ?? getDemoCompany(previewKey)?.name ?? previewKey
    return (
      <div className={railBar}>
        <span className={chip}>Preview</span>
        <span className="font-medium">
          Previewing as <strong className="font-semibold text-text-primary">{previewLabel}</strong> — their product, live
        </span>
        <button onClick={exitClientPreview} className={`ml-auto ${exitBtn}`}>
          Exit preview <X size={13} />
        </button>
      </div>
    )
  }

  // ── Acting-as strip (demo + sandbox) ───────────────────────────────────
  // Both non-production environments have an "act as someone" concept, so both
  // get the same banner: demo flips persona, sandbox flips the sandbox-org user.
  const isDemo = appMode === 'demo'
  const isSandbox = appMode === 'sandbox'
  if (!isDemo && !isSandbox) return null

  // Per-mode descriptors.
  const emp = isDemo && currentEmployeeId ? timesheetDataService.getEmployee(currentEmployeeId) : undefined
  const chipLabel = isDemo ? 'Demo' : 'Sandbox'
  const noun = isDemo ? 'persona' : 'user'
  const btnLabel = isDemo ? 'Switch persona' : 'Switch user'
  const popHeader = isDemo ? 'Acting as a persona' : 'Act as a user'
  const actingName = isDemo ? (emp?.name ?? (currentUser.id !== 'demo_user' ? currentUser.name : '')) : currentUser.name
  const actingRole = isDemo && emp ? friendlyRole(deriveSystemRole(emp)) : null

  return (
    <div ref={ref} className={`relative ${railBar}`}>
      <span className={chip}>{chipLabel}</span>
      <span className="font-medium truncate">
        {actingName ? (
          <>Acting as <strong className="font-semibold text-text-primary">{actingName}</strong>{actingRole && <span className="text-text-tertiary"> · {actingRole}</span>}</>
        ) : (
          <span className="text-text-tertiary">No {noun} selected</span>
        )}
      </span>

      <div className="ml-auto flex items-center gap-2 flex-shrink-0">
        {/* Phones only: the top bar has no width for the org chips (they
            collide with search at 390px), so company switching lives on this
            row below sm. Both chips self-gate by mode. */}
        <span className="sm:hidden"><DemoCompanySwitcher /></span>
        <span className="sm:hidden"><TunnelOrgChip /></span>
        <div className="relative">
          <button
            onClick={() => setMenu((m) => (m === 'persona' ? null : 'persona'))}
            className={ghostBtn}
            aria-expanded={menu === 'persona'}
          >
            <UserCog size={13} /> {btnLabel} <ChevronDown size={12} className={cn('transition-transform', menu === 'persona' && 'rotate-180')} />
          </button>
          {menu === 'persona' && (
            <div className="absolute right-0 top-full mt-1.5 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-dropdown border border-border z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-wider text-text-tertiary font-semibold">
                {popHeader}
              </div>
              {isDemo ? <PersonaPicker onPicked={() => setMenu(null)} /> : <SandboxUserPicker onPicked={() => setMenu(null)} />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
